/**
*****************************************
*                                       *
* NAGARRO  - CSX - CONFIDENTIAL         *
*                                       *
*****************************************
*/

import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';

import { ConfigService } from './../../services/config.service';
import { DataStoreService } from './../../services/datastore.service';
import { EditModeService } from '../../services/edit-mode.service';
import { LmsEventDetails } from './../../model/lms-event-details.model';
import { LmsLoaderService } from './../../services/lms-loader.service';
import { LmsMenuService } from './../../../core/services/lms-menu.service';
import { LmsTabsDragDropService } from './../../services/lms-tabs-drag-drop.service';
import { MenuItem } from '../../model/menu-item.model';
import { PopupService } from '../../../core/services/popup.service';
import { Router } from '@angular/router';
import { Tab } from './../../model/tab.model';
import { environment } from './../../../../environments/environment';
import { DragDropModule } from 'primeng/dragdrop';

/**
 * This class created for individual tab which are shown in header tabs.
 * @export
 * @class LmsTabComponent
 * @implements {OnInit}
 * @implements {OnChanges}
 * @implements {OnDestroy}
 */
@Component({
  selector: 'lms-tab',
  templateUrl: './lms-tab.component.html',
  styleUrls: ['./lms-tab.component.scss']
})

export class LmsTabComponent implements OnInit, OnChanges, OnDestroy {

  @Input() tab: Tab;

  @Input() tabs: Tab[];

  @Input() indexOfTab: number;

  selectedTab: Tab;

  subTypes: string[];

  @Output() lmsTabChange = new EventEmitter<LmsEventDetails>();

  @Output() tabFocusOut = new EventEmitter<any>();

  searchText: string;

  popOutOps: any;

  dragCancelled = false;

  showWarnPop = false;
  showDeletePop = false;

  warnMessage = '';

  deleteWarnMessage = 'Unsaved data will be lost.Do you want to continue?';

  previousTab: any;
  tabLevelDialog: number;

  /**
   * Creates an instance of LmsTabsDragDropService, DataStoreService,
   * ConfigService, LmsMenuService
   * @param {LmsTabsDragDropService} lmsTabsDragDropService
   * @param {DataStoreService} dataStoreService
   * @param {ConfigService} configService
   * @param {LmsMenuService} lmsMenuService
   * @memberOf LmsTabComponent
   */
  constructor(private lmsTabsDragDropService: LmsTabsDragDropService,
    private dataStoreService: DataStoreService,
    private configService: ConfigService,
    private lmsMenuService: LmsMenuService,
    private router: Router,
    private editModeService: EditModeService,
    private lmsLoaderService: LmsLoaderService,
    private popUpService: PopupService) {
    this.tabLevelDialog = this.configService.getConfigData().tabIndex.dialogBox;
  }

  /**
   * This method is used to emit an event when focus comes
   * on this template to inform the parent so that parent
   * can control the carousal movement.
   * @param {Tab} tab
   * @memberOf LmsTabComponent
   */
  public focusOutEvent(tab: Tab) {
    this.tabFocusOut.emit(tab);
  }
  /**
   * This method invoked when component is load.
   * @memberOf LmsTabComponent
   */
  ngOnInit() {
    this.init();
  }

  /**
   * This method invoked when any change is done in tabs.
   * @param {SimpleChanges} changes
   * @memberOf LmsTabComponent
   */
  ngOnChanges(changes: SimpleChanges) {
    const tabChanged: boolean = changes['tabs'].currentValue !== changes['tabs'].previousValue;
    if (tabChanged) {
      this.init();
    }
  }

  /**
   * Method initialize the selected tab, filter tabs (contains tabs according to type),
   * sub types and trigger search method.
   * @private
   * @memberOf LmsTabComponent
   */
  private init() {
    if (this.tabs) {
      this.selectedTab = this.tabs.find((tab) => { return tab && tab.selected; });
    }
    if (this.selectedTab) {
      const element = document.getElementById(`topTab${this.selectedTab.id}`);
      if (element) {
        element.focus();
      }
      this.tabs = this.tabs.filter((tab) => { return this.selectedTab && this.selectedTab.type === tab.type; });
      this.subTypes = (<MenuItem>(this.lmsMenuService.getMenuItem(this.selectedTab.type))).subTypes;
      this.searchText = this.selectedTab && this.selectedTab.tabSearchParameter ?
        this.selectedTab.tabSearchParameter.searchText :
        '';
      this.search(false);
    }
  }

  /**
   * This method update data container as per selected tab, isRefersh indicates
   * that data is fetched from service or from datastore.
   * @param {boolean} isRefresh
   * @memberOf LmsTabComponent
   */
  search(isRefresh: boolean) {
    if (this.searchText) {
      this.selectedTab.tabSearchParameter.searchText = this.searchText;
    }
    this.lmsTabChange.emit(<LmsEventDetails>({ type: this.configService.getConfigData().tab.event.update, data: this.selectedTab }));
  }

  /**
   * This method invoked when tab dragging start.
   * @param {any} e
   * @param {any} element
   * @param {any} tab
   * @memberOf LmsTabComponent
   */
  tabDragStart(e, element, tab) {
    if (!this.editModeService.isEditMode) {
      tab.dropData = this.dataStoreService.getValue(tab);
      const dragData = JSON.stringify(tab);
      this.lmsTabsDragDropService.writeToLocalStorage(dragData);
    } else {
      this.popOutOps = {
        tab: tab
      };
      this.previousTab = tab;
      this.dragCancelled = true;
      this.showWarnPop = true;
      this.warnMessage = 'There are unsaved changes on this tab, Please save them before drag out or your changes will be lost';
    }
  }

  /**
   * This method invoked when tab drag end.
   * @param {any} tab
   * @memberOf LmsTabComponent
   */
  tabDragEnd(tab, event?: any) {
    if (event) {
      event.preventDefault();
    }
    setTimeout(() => {
      const json = localStorage.getItem('localTab');
      if (json && this.lmsTabsDragDropService.tabDragStarted && !this.dragCancelled) {
        this.lmsTabsDragDropService.setTabDragStarted(false);
        delete tab.dropData;
        this.removeTab(tab);
        this.lmsTabsDragDropService.openTabInNewWindow();
      } else {
        if (!this.dragCancelled) {
          this.lmsTabsDragDropService.setTabDragStarted(false);
          delete tab.dropData;
          this.removeTab(tab);
        }
      }
    }, 500);
  }

  /**
   * @memberOf LmsTabComponent
   */
  hidePopup() {
    if (this.showDeletePop || this.showWarnPop) {
      this.dragCancelled = false;
      this.showWarnPop = false;
      this.warnMessage = '';
      this.popOutOps = {};
      this.previousTab = null;
      this.showDeletePop = false;
      this.popUpService.setPageMode('');
      this.popUpService.setSubmitBtnId('');
    }
  }

  setPageMode(btnId: string) {
    if (btnId && btnId.length > 0) {
      this.popUpService.setPageMode('Dialog');
      this.popUpService.setSubmitBtnId(btnId);
    } else {
      this.popUpService.setPageMode('');
      this.popUpService.setSubmitBtnId('');
    }
  }

  /**
   * @memberOf LmsTabComponent
   */
  goForPopoutOps() {
    this.editModeService.setEditMode({ status: false, message: '' });
    this.previousTab.dropData = this.dataStoreService.getValue(this.previousTab);
    const dragData = JSON.stringify(this.previousTab);
    this.lmsTabsDragDropService.writeToLocalStorage(dragData);
    delete this.previousTab.dropData;
    this.removeTab(this.previousTab);
    this.hidePopup();
    this.lmsTabsDragDropService.setTabDragStarted(false);
    this.lmsTabsDragDropService.openTabInNewWindow();
  }

  /**
   * This method remove tab from tabstore and data from data store.
   * @param {Tab} tab
   * @memberOf LmsTabComponent
   */
  removeTab(tab: Tab, deleteType?: boolean) {

    if (deleteType) {
      this.editModeService.setEditMode({ status: false, message: '' });
    }

    if (this.editModeService.isEditMode) {
      this.showDeletePop = true;
      setTimeout(() => {
        // to hide the loader with popup
        this.lmsLoaderService.hideLoader();
      });

      return;
    } else {
      this.hidePopup();
      if (this.tabs.length === 1) {
        this.addTab(tab.subType);
      } else if (tab && tab.selected) {
        const lastTab = (this.tabs.indexOf(tab) === 0) ? this.tabs[this.tabs.indexOf(tab) + 1] : this.tabs[this.tabs.indexOf(tab) - 1];
        this.router.navigateByUrl(`${lastTab.type.toLowerCase()}/${lastTab.subType.toLowerCase()}/${lastTab.id}`,
          { skipLocationChange: environment.hideUrl });
      }
      this.lmsTabChange.emit(<LmsEventDetails>({ type: this.configService.getConfigData().tab.event.remove, data: tab }));
      this.dataStoreService.removeTab(tab);
    }
  }
  /**
   * This method invoked for creates a new tab with subtype and search text.
   * @param {string} subType
   * @memberOf LmsTabComponent
   */
  addTab(subType: string) {
    this.searchText = '';
    this.router.navigateByUrl(
      `/${this.selectedTab.type.toLowerCase()}/${subType.toLowerCase()}/${new Date().getTime()}`,
      { skipLocationChange: environment.hideUrl });
  }
  /**
   * This method set selected tab and emits when an event.
   * @param {Tab} tab
   * @memberOf LmsTabComponent
   */
  selectTab(tab: Tab) {
    this.router.navigateByUrl(`${tab.type.toLowerCase()}/${tab.subType.toLowerCase()}/${tab.id}`,
      { skipLocationChange: environment.hideUrl });
  }

  /**
   * Destroy the state of lifecycle hook.
   * @memberOf LmsTabComponent
   */
  ngOnDestroy() { }
}
